// src/components/Department/EditDepartmentModal.jsx
// Modal component for adding/editing a single department
import React, { useState, useEffect } from 'react';

/**
 * EditDepartmentModal Component
 * A modal dialog for creating or editing a department
 *
 * @param {boolean} isOpen - Controls whether modal is visible
 * @param {Function} onClose - Callback when modal should close
 * @param {Function} onSave - Callback when form is submitted
 * @param {Object} department - Existing department data (for edit mode)
 * @param {boolean} isSaving - Whether save operation is in progress
 */
const EditDepartmentModal = ({
  isOpen,
  onClose,
  onSave,
  department,
  isSaving,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
  });

  // Populate form when editing
  useEffect(() => {
    if (department) {
      setFormData({
        name: department.name || '',
        isActive: Boolean(department.isActive),
      });
    } else {
      setFormData({
        name: '',
        isActive: true,
      });
    }
  }, [department]);

  if (!isOpen) return null;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter a department name');
      return;
    }

    // SEND SINGLE OBJECT ONLY
    onSave({
      name: formData.name,
      isActive: formData.isActive,
    });

    // CLEAR FORM AFTER SUCCESS
    setFormData({
      name: '',
      isActive: true,
    });
  };

  // Handle modal close
  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-content">
        {/* Modal Header */}
        <header className="inventory-modal-header">
          <h3 className="inventory-modal-title">
            {department ? `Edit Department: ${department.name}` : 'Edit Department'}
          </h3>
          <button
            className="inventory-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </header>

        {/* Modal Body */}
        <div className="inventory-modal-body">
          <form id="departmentForm" onSubmit={handleSubmit}>
            <div className="inventory-form-grid">
              {/* Department Name Field */}
              <label htmlFor="name">
                Department Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="inventory-custom-input"
                placeholder="e.g., HR, Finance, IT"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSaving}
              />

              {/* Active Checkbox */}
              <label htmlFor="isActive">Active</label>
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleCheckboxChange}
                disabled={isSaving}
              />
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <footer className="inventory-modal-footer">
          <button
            className="inventory-btn inventory-btn-cancel"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>
          <button
            className="inventory-btn inventory-btn-save"
            disabled={isSaving}
            type="submit"
            form="departmentForm"
          >
            {isSaving ? 'Saving...' : 'Save Department'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default EditDepartmentModal;
